import React, { useState } from "react";
import Calendar from "../common/Calendar";
import { formatDateRange, formatTime } from "../../utils/HelperMethod";
import UnifiedCalendar from "../common/Calendar";
import { ClockIcon } from "@heroicons/react/24/outline";
import { HiOutlineCalendar, HiOutlineClock } from "react-icons/hi";
import { FiChevronDown, FiXCircle } from "react-icons/fi";
import CustomButton from "../common/CustomButton";

export default function ExperienceAvailability({
  calendarMeta,
  availabilityRules,
  people,
  setPeople,
  estimatedDuration,
  durationError,
  selectedDate,
  setSelectedDate,
  addOns = [],
  selectedAddOnIds,
  setSelectedAddOnIds,
  onBookNow,
  loading,
  price,
  isConnected,
  specificStartTime,
  startTimes,
  selectedStartTime,
  setSelectedStartTime,
}) {
  const date = calendarMeta?.startDate;

  const [timeDropdownOpen, setTimeDropdownOpen] = React.useState(false);
  const month = new Date(date).getMonth();

  return (
    <div className="bg-white border rounded-2xl p-5 space-y-4">
      {/* RULE CHIPS */}
      <div className="flex flex-wrap gap-3">
        {availabilityRules?.map((r) => (
          <div
            key={r.experienceAvailabilityRuleId}
            className="
        flex flex-col gap-1
        rounded-xl border border-gray-200
        bg-gray-50 px-3 py-2
        text-xs text-gray-700
      "
          >
            {/* Time */}
            <div className="flex items-center gap-1">
              <span>
                <HiOutlineClock className="w-4 h-4 text-toucanTeal" />
              </span>
              <span>
                {formatTime(r.startTime)} – {formatTime(r.endTime)}
              </span>
            </div>

            {/* Date Range */}
            <div className="flex items-center gap-1 text-gray-500">
              <span>
                <HiOutlineCalendar className="w-4 h-4 text-toucanTeal" />
              </span>
              <span>{formatDateRange(r.startDate, r.endDate)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* CALENDAR (FIXED) */}
      <UnifiedCalendar
        mode="select" // 👈 IMPORTANT (sirf user booking ke liye)
        selectedDate={selectedDate}
        onSelectDate={setSelectedDate}
        selectableDates={calendarMeta?.selectableDates || []}
        disabledDates={calendarMeta?.disabledDates || []}
        initialMonth={month}
        useInitialMonth={true}
        preSelectedDates={calendarMeta.selectableDates} // 👈 ye important
      />

      {/* START TIME DROPDOWN */}
      {specificStartTime && startTimes?.length > 0 && (
        <div className="space-y-2">
          <label className="text-sm font-semibold">Start Time</label>

          <div className="flex flex-wrap gap-2">
            {startTimes.map((time) => {
              const isSelected = selectedStartTime === time;

              return (
                <button
                  key={time}
                  type="button"
                  onClick={() => setSelectedStartTime(time)}
                  className={`
              px-3 h-8
              text-xs font-medium
              rounded-md
              border
              transition
              ${
                isSelected
                  ? "bg-toucanTeal text-white border-toucanTeal"
                  : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
              }
            `}
                >
                  {time}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* GUESTS */}
      <div>
        <label className="text-sm font-semibold">Guests</label>
        <input
          type="number"
          min={1}
          value={people}
          onChange={(e) => setPeople(Number(e.target.value))}
          className="w-full mt-1 border rounded-lg h-10 px-3"
        />
      </div>

      {/* DURATION */}
      <div className="text-sm">
        {durationError ? (
          <span className="text-red-500">{durationError}</span>
        ) : (
          <span>
            Estimated Duration: <strong>{estimatedDuration} minutes</strong>
          </span>
        )}
      </div>

      {/* ADDONS */}
      {addOns.length > 0 && (
        <div className="space-y-3">
          {addOns.map((a) => {
            const isSelected = selectedAddOnIds.includes(a.experienceAddOnId);

            return (
              <div
                key={a.experienceAddOnId}
                onClick={() =>
                  setSelectedAddOnIds((prev) =>
                    isSelected
                      ? prev.filter((x) => x !== a.experienceAddOnId)
                      : [...prev, a.experienceAddOnId],
                  )
                }
                className={`
            cursor-pointer
            border rounded-xl p-4
            flex items-center justify-between
            transition
            ${
              isSelected
                ? "border-[#1FADC6] bg-[#1FADC6]/5"
                : "border-gray-200 hover:border-gray-300"
            }
          `}
              >
                {/* LEFT TEXT */}
                <div>
                  <div className="text-sm font-semibold text-gray-900">
                    {a.name}
                  </div>
                  <div className="text-xs text-gray-500">Optional add-on</div>
                </div>

                {/* PRICE */}
                <div className="text-sm font-semibold text-gray-900">
                  ${Number(a.price).toFixed(0)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* PRICE SUMMARY */}
      <div className="mt-6 border-t pt-4 space-y-2">
        {/* UNIT PRICE */}
        <div className="flex justify-between text-gray-700 text-sm sm:text-base">
          <span>Price</span>
          <span>${Number(price || 0).toFixed(2)}</span>
        </div>

        {/* QUANTITY */}
        <div className="flex justify-between text-gray-600 text-sm">
          <span>
            {people} {people > 1 ? "Guests" : "Guest"}
          </span>
          <span>${(Number(price || 0) * Number(people || 1)).toFixed(2)}</span>
        </div>

        {/* ADD-ONS */}
        {selectedAddOnIds?.length > 0 && (
          <div className="flex justify-between text-gray-600 text-sm">
            <span>Add-ons</span>
            <span>
              $
              {addOns
                .filter((a) => selectedAddOnIds.includes(a.experienceAddOnId))
                .reduce((sum, a) => sum + Number(a.price || 0), 0)
                .toFixed(2)}
            </span>
          </div>
        )}

        {/* TOTAL */}
        <div className="flex justify-between pt-2 border-t text-sm sm:text-base font-semibold text-gray-900">
          <span>Total</span>
          <span>
            $
            {(
              Number(price || 0) * Number(people || 1) +
              addOns
                .filter((a) => selectedAddOnIds.includes(a.experienceAddOnId))
                .reduce((sum, a) => sum + Number(a.price || 0), 0)
            ).toFixed(2)}
          </span>
        </div>
      </div>

      {/* CTA */}
      <CustomButton
        variant="primary"
        disabled={
          !selectedDate ||
          (specificStartTime && !selectedStartTime) ||
          durationError ||
          loading ||
          !isConnected
        }
        loading={loading}
        onClick={onBookNow}
        className="w-full h-11 bg-[#00B8C8] text-white rounded-lg disabled:opacity-50"
      >
        Book Now
      </CustomButton>
    </div>
  );
}
